import { z } from "zod";

import { openai } from "@/schemas";
import { PromptBlueprint, RequestErrorType, RequestLogStatus } from "@/types";

const providers = ["openai", "langchain", "anthropic"] as const;

const requestLogStatusSchema = z.enum(["SUCCESS", "WARNING", "ERROR"]);
const requestErrorTypeSchema = z.enum([
  "PROVIDER_TIMEOUT",
  "PROVIDER_QUOTA_LIMIT",
  "PROVIDER_RATE_LIMIT",
  "PROVIDER_ERROR",
  "TEMPLATE_RENDER_ERROR",
  "VARIABLE_MISSING_OR_EMPTY",
  "UNKNOWN_ERROR",
]);

const base = z.object({
  id: z.number(),
  function_name: z.string(),
  request_start_time: z.string(),
  request_end_time: z.string(),
  engine: z.string(),
  tags: z.string().array(),
  is_starred: z.boolean(),
  prompt_id: z.number().nullable(),
  response_string: z.string(),
  workspace_id: z.number().nullable(),
  prompt_version_number: z.number().nullable(),
  metadata: z.union([z.null(), z.array(z.null()), z.array(z.record(z.string()))]),
  latency: z.number().optional(),
  status: requestLogStatusSchema.optional(),
  error_type: requestErrorTypeSchema.nullable().optional(),
  error_message: z.string().max(1024).nullable().optional(),
});

const completion = base.extend({
  provider_type: z.enum([providers[0], providers[1]]),
  is_completion: z.literal(true),
  is_chat: z.undefined(),
  request_text: z.string(),
  response_text: z.string().optional(),
  type: z.enum(["request", "group"]),
  cardinality: z.undefined(),
});

const chatCompletion = base.extend({
  provider_type: z.enum(providers),
  is_completion: z.undefined(),
  is_chat: z.literal(true),
  request_text: z
    .object({
      role: z.enum(openai.roles),
      content: z.string(),
      parts: z
        .object({
          text: z.string().optional(),
        })
        .array()
        .optional(),
    })
    .array(),
  response_text: openai.message.or(
    z.object({
      parts: z
        .object({
          text: z.string().optional(),
        })
        .array(),
    }),
  ),
  type: z.enum(["request", "group"]),
  cardinality: z.undefined(),
});

const group = base.extend({
  provider_type: z.undefined(),
  is_completion: z.undefined(),
  is_chat: z.literal(false),
  request_text: z.string(),
  response_text: z.string().optional(),
  type: z.enum(["request", "group"]),
  cardinality: z.number(),
});

const schema = z.discriminatedUnion("is_chat", [completion, chatCompletion, group]);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const promptTemplate = z.discriminatedUnion("is_chat", [completion, chatCompletion]).and(
  z.object({
    prompt_input_variables: z.record(z.any()),
    score: z.number(),
    request_response: openai.message,
    request_response_preview: z.string(),
  }),
);

type PromptTemplate = z.infer<typeof promptTemplate> & {
  prompt_version: PromptBlueprint | null;
  prompt_name: string | null;
  price: number;
  input_tokens: number;
  output_tokens: number;
  tokens: number;
  tags_array: string[];
  latency?: number;
  status?: RequestLogStatus;
  error_type?: RequestErrorType | null;
  error_message?: string | null;
};

export { PromptTemplate, providers, schema };
